import { mergeTranslations } from "ra-core";
import polyglotI18nProvider from "ra-i18n-polyglot";
import englishMessages from "ra-language-english";
import frenchMessages from "ra-language-french";
import russianMessages from "ra-language-russian";
import { raSupabaseEnglishMessages } from "ra-supabase-language-english";
import { raSupabaseFrenchMessages } from "ra-supabase-language-french";
import { englishCrmMessages } from "./englishCrmMessages";
import { frenchCrmMessages } from "./frenchCrmMessages";
import { russianCrmMessages } from "./russianCrmMessages";

// Messages of the ra-supabase screens (password reset / OAuth consent) that
// the «Всё видно» build can still reach.
const raSupabaseRussianMessages = {
  "ra-supabase": {
    auth: {
      email: "Почта",
      confirm_password: "Повторите пароль",
      sign_in_with: "Войти через %{provider}",
      forgot_password: "Забыли пароль?",
      reset_password: "Сбросить пароль",
      password_reset: "Проверьте почту: мы отправили письмо для сброса пароля.",
      missing_tokens: "Нет токенов доступа",
      back_to_login: "Назад ко входу",
    },
    reset_password: {
      forgot_password: "Забыли пароль?",
      forgot_password_details: "Введите почту, мы пришлём инструкцию.",
    },
    set_password: {
      new_password: "Придумайте пароль",
    },
    validation: {
      password_mismatch: "Пароли не совпадают",
    },
    oauth: {
      authorize: "Разрешить доступ приложению",
      authorize_details:
        "Приложение запрашивает доступ к вашей учётной записи.",
      access_scope:
        "Оно сможет читать и изменять ваши данные с теми же правами, что и ваша учётная запись.",
      approved: "Доступ разрешён",
      close_tab: "Эту вкладку можно закрыть.",
      no_request: "Запрос на доступ не найден",
    },
  },
};

const raSupabaseEnglishMessagesOverride = {
  "ra-supabase": {
    auth: {
      password_reset: "Check your emails for a Reset Password message.",
    },
    oauth: {
      authorize: "Authorize Application",
      authorize_details: "This application wants to access your account.",
      access_scope:
        "It will be able to read and modify your data, with the same permissions as your account.",
      approved: "Authorization Approved",
      close_tab: "You can now close this tab.",
      no_request: "No authorization request found",
    },
  },
};

const raSupabaseFrenchMessagesOverride = {
  "ra-supabase": {
    auth: {
      password_reset:
        "Consultez vos emails pour trouver le message de reinitialisation du mot de passe.",
    },
    oauth: {
      authorize: "Autoriser l'application",
      authorize_details: "Cette application souhaite accéder à votre compte.",
      access_scope:
        "Elle pourra lire et modifier vos données, avec les mêmes droits que votre compte.",
      approved: "Autorisation accordée",
      close_tab: "Vous pouvez fermer cet onglet.",
      no_request: "Aucune demande d'autorisation trouvée",
    },
  },
};

const englishCatalog = mergeTranslations(
  englishMessages,
  raSupabaseEnglishMessages,
  raSupabaseEnglishMessagesOverride,
  englishCrmMessages,
);

const frenchCatalog = mergeTranslations(
  englishCatalog,
  frenchMessages,
  raSupabaseFrenchMessages,
  raSupabaseFrenchMessagesOverride,
  frenchCrmMessages,
);

const russianCatalog = mergeTranslations(
  englishCatalog,
  russianMessages,
  raSupabaseRussianMessages,
  { ra: { auth: { email: "Почта" } } },
  russianCrmMessages,
);

// «Всё видно» is Russian-only: the interface does not follow the browser
// language. The en and fr catalogs stay loadable (changeLocale) but are not
// offered in the language switcher.
export const getInitialLocale = (): "ru" => "ru";

export const i18nProvider = polyglotI18nProvider(
  (locale) => {
    if (locale === "fr") {
      return frenchCatalog;
    }
    if (locale === "en") {
      return englishCatalog;
    }
    return russianCatalog;
  },
  getInitialLocale(),
  [{ locale: "ru", name: "Русский" }],
  { allowMissing: true },
);

// Component tests assert on behavior through stable English wording; the
// Russian catalog is covered by russianCrmMessages.test.ts (key coverage) and
// i18nProvider.test.ts.
export const testI18nProvider = polyglotI18nProvider(
  () => englishCatalog,
  "en",
  [{ locale: "en", name: "English" }],
  { allowMissing: true },
);
